import type { TFunction } from 'i18next';
import { PROJECT_NAME } from '../../config';
import type { QuestRewardType } from '../../models/quest';

export interface GetQuestsDataParams {
  t: TFunction;
}

/**
 * Local display catalog for quests (titles, dual rewards, links).
 * Not the API contract — secondaryReward* are frontend-only and absent from
 * generated DailyQuestWithProgressResponse.
 */
export type QuestCatalogEntry = {
  questId: number;
  title: string;
  description: string;
  rewardAmount: number;
  rewardType: QuestRewardType;
  secondaryRewardAmount?: number;
  secondaryRewardType?: Exclude<QuestRewardType, "vip">;
  hasLink?: boolean;
};

// Масив з назвами та описами квестів (dual rewards: all quests give both XP + pearls)
export const getQuestsData = ({ t }: GetQuestsDataParams): QuestCatalogEntry[] => [
  {
    questId: 1,
    title: t("Is it time for mayor elections?"),
    description: t("Visit the political center of Web3Islands and find out at the Town Hall if mayor elections are currently taking place, and also meet the current mayor"),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 2,
    title: t("Visit the Shop"),
    description: t("Enter the Shop location on the map."),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 3,
    title: t("Path to the top of the rankings"),
    description: t("Visit the Hall of Fame (Top-100 leaderboard) and find out about your places in the web3islands rankings"),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 4,
    title: t("Visit 3 House Interiors"),
    description: t("Enter the interior of 3 different players' houses."),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 5,
    title: t("Novice Pearl Hunter"),
    description: t("Tap the pearl shell at least 100 times to complete this quest"),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 6,
    title: t("Join the Community Group"),
    description: t("Become a member of the official Web3Islands channel in Telegram."),
    rewardAmount: 15000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
    hasLink: true,
  },
  {
    questId: 7,
    title: t("Get to know our friends' project"),
    description: t("Just spend some time in the app from our friends:"),
    rewardAmount: 30000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 10000,
    secondaryRewardType: "exp" as const,
    hasLink: true,
  },
  {
    questId: 8,
    title: `${t("Reward for watching an ad every hour")} 🎁`,
    description: t("Click the button below to begin watching. This quest is available to complete every hour"),
    rewardAmount: 10000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 41,
    title: `${t("Reward for partner ad quest")} 🎁`,
    description: t("This is an external quest that changes each time. Please read what is required below and complete this simple action to receive a great reward from us!"),
    rewardAmount: 10000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 208,
    title: t("Reward for following y0news AI on X"),
    description: t("Subscribe to X:"),
    rewardAmount: 30000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 10000,
    secondaryRewardType: "exp" as const,
    hasLink: true,
  },
  {
    questId: 209,
    title: t("Reward for following y0news Crypto on X"),
    description: t("Subscribe to X:"),
    rewardAmount: 30000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 10000,
    secondaryRewardType: "exp" as const,
    hasLink: true,
  },
  {
    questId: 106,
    title: t("Expert Pearl Hunter"),
    description: t("Tap the pearl shell at least 1000 times to complete this quest"),
    rewardAmount: 15000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 10000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 107,
    title: t("Master Pearl Hunter"),
    description: t("Tap the pearl shell at least 10000 times to complete this quest"),
    rewardAmount: 40000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 25000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 108,
    title: t("Join the Community Chat (English Language)"),
    description: t("Here you can chat and find new friends from around the world") + " ❤️ " + t("Free plot announcements are posted there regularly — be the first to grab one!"),
    rewardAmount: 15000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
    hasLink: true,
  },
  {
    questId: 140,
    title: t("Run for Mayor!"),
    description: t("Step into the mayoral race: register your candidacy and claim your reward. And don't forget to invite friends to vote for you!"),
    rewardAmount: 200000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 50000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 139,
    title: t("Cast Your Vote!"),
    description: t("Simply vote for your favorite candidate and get a reward! This quest can be completed every day"),
    rewardAmount: 5000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 141,
    title: t("Become the Mayor!"),
    description: t("Become the winner of the mayor elections and become the most famous and influential person in the {{projectName}} universe!", { projectName: PROJECT_NAME }),
    rewardAmount: 1000000,
    rewardType: "exp" as const,
    secondaryRewardAmount: 200000,
    secondaryRewardType: "currency" as const,
  },
  {
    questId: 172,
    title: t("Allow the bot to send news"),
    description: t("Just write any message to our chat bot and always stay up to date with the news!"),
    rewardAmount: 15000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 200,
    title: "\uD83D\uDD25 " + t("Invite 3 friends and get VIP Status for 3 days!"),
    description: t("Invite friends and earn passive income. This quest can be completed multiple times!"),
    rewardAmount: 0,
    rewardType: "vip" as const,
  },
  {
    questId: 205,
    title: t("Decorate Your House Interior"),
    description: t("Purchase at least one item from the furniture shop."),
    rewardAmount: 100000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 30000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 206,
    title: t("Cobweb Destroyer"),
    description: t("Remove cobwebs from players' house interiors 10 times. Cobwebs appear in houses whose owners haven't logged in for a long time. To remove a cobweb, press and hold it, then select the broom icon."),
    rewardAmount: 5000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 3000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 207,
    title: t("Duel Champion"),
    description: t("Win 5 duels and prove you are the fastest duelist on the island!"),
    rewardAmount: 5000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 3000,
    secondaryRewardType: "exp" as const,
  },
  {
    questId: 210,
    title: t("Have a Chat with the Mayor"),
    description: t("Visit the Town Hall and have a conversation with the mayor. Ask about life on the island, current events, or just share your thoughts!"),
    rewardAmount: 10000,
    rewardType: "currency" as const,
    secondaryRewardAmount: 5000,
    secondaryRewardType: "exp" as const,
  },
];
